// Import the required components and services
import { GenericPortalError } from "../components/error-display/PortalError";

import { authenticationService } from "./AuthenticationService";
import { promptService } from "./PromptService";

// Define the EmbeddingService class
class EmbeddingService {
  // Function to render a dossier
  async renderDossier(
    placeholder,
    samlLoginURL,
    mstrLibraryUrl,
    projectId,
    dossierId,
    currentDossier,
    token,
    dossierInstance,
    mode = "consumption",
  ) {
    try {
      // Destroy the current dossier if it exists
      // if (currentDossier != null && currentDossier[dossierId])
      //   window.microstrategy.dossier.destroy(currentDossier[dossierId].parent);

      // Construct the dossier URL
      let dossierUrl = `${mstrLibraryUrl}${projectId}/${dossierId}`;

      // Dossier embedding configuration
      let config = {
        placeholder: placeholder,
        url: dossierUrl,
        enableCustomAuthentication: true,
        customAuthenticationType: "loginWithAuthToken",
        disableNotification: true,
        enableResponsive: true,
        dossierRenderingMode: mode,
        getLoginToken: () => {
          // Custom authentication using token or SAML login
          if (token) {
            return Promise.resolve(token);
          } else {
            return window.microstrategy.auth.samlLogin(samlLoginURL);
          }
        },
        filterFeature: {
          enabled: false,
          edit: false,
          summary: true,
        },
      };

      // Override config with dossier instance if provided
      if (dossierInstance != null) {
        config = {
          ...config,
          instance: dossierInstance,
        };
      }

      // Handle the case when the placeholder is not present
      if (!placeholder) {
        return;
      }

      // Create and render the dossier
      let dossier = await window.microstrategy.dossier.create(config);

      // Register event handler for location change events
      function eventEmbedLocationChangedFunction(e) {
        console.log("Embed Location Changed Event");
      }
      dossier.registerEventHandler(
        window.microstrategy.dossier.EventType.ON_EMBED_LOCATION_CHANGED,
        eventEmbedLocationChangedFunction,
      );

      return dossier;
    } catch (e) {
      throw GenericPortalError("EMBEDDING_DOSSIER_ERROR", "warning", "snackbar");
    }
  }

  // Function to render a dossier using the native library page
  async renderDossierNativeLibraryPage(config, placeholder) {
    window.microstrategy.embeddingContexts.embedLibraryPage({
      serverUrl: config.backendServiceURL,
      placeholder: placeholder,
      enableCustomAuthentication: true,
      customAuthenticationType: window.microstrategy.dossier.CustomAuthenticationType.AUTH_TOKEN,
      getLoginToken: () => window.microstrategy.auth.samlLogin(config.samlLoginURL),
    });
  }

  // Function to render a native dossier
  async renderDossierNative(
    placeholder,
    projectId,
    dossierId,
    instanceId,
    environment,
    isMultidashboard,
    visualizations,
    dossierEmbedded,
    setDossierEmbedded,
  ) {
    let mstrDossier = null,
      containers = [];

    try {
      // Unload the existing embedded dossier if present
      if (dossierEmbedded != null) {
        await environment.unloadDossier(dossierEmbedded);
        if (placeholder.children.length > 0)
          while (placeholder.children.length > 0) {
            placeholder.removeChild(placeholder?.children[0]);
          }
      }

      // Enable multiple dossiers embedding if required
      window.microstrategy.nativeEmbedding.featureFlags.multipleDossiers = isMultidashboard;

      // Load the new dossier
      mstrDossier = await environment.loadDossier({
        projectId: projectId,
        objectId: dossierId,
        instanceId: instanceId,
      });

      let dossierDef = await mstrDossier.getDossierDefinition();

      // Save the embedded dossier state
      setDossierEmbedded(mstrDossier);

      // Create containers for visualizations
      visualizations.forEach((visKey) => {
        let container = document.createElement("div");

        container.classList.add("vis-container");
        placeholder.appendChild(container);
        containers.push({
          key: visKey,
          container: container,
        });
      });

      // Refresh the dossier to render the visualizations
      await mstrDossier.refresh(containers);
    } catch (e) {
      console.log(e);
      throw new GenericPortalError("EMBEDDING_NATIVE_DOSSIER_ERROR");
    }

    return mstrDossier;
  }

  // Function to get or create an embedding environment
  async getEnvironment(config, token, existingEnvironment, setEnvironment) {
    let environment = existingEnvironment;

    // Create a new environment if it does not exist
    if (environment == null || environment == undefined) {
      environment = await window.microstrategy.embeddingComponent.environments.create({
        serverUrl: config.backendServiceURL,
        getAuthToken: () => {
          // Custom authentication using token or SAML login
          if (token) {
            return Promise.resolve(token);
          } else {
            return window.microstrategy.auth.samlLogin(config.samlLoginURL);
          }
        },
      });

      // Save the environment state
      setEnvironment(environment);

      return environment;
    }

    return environment;
  }
}

// Export an instance of EmbeddingService
export const embeddingService = new EmbeddingService();
